"use client";

import { useEffect, useState } from "react";

import { CommonDialog, JetSmartButton } from "@repo/ui/components";

/**
 * Component that requests location permission when the main page loads.
 * Shows a popup modal asking the user for location access.
 */
export function LocationPermission() {
  const [showModal, setShowModal] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") return;

    // Check if geolocation is available
    if (!navigator.geolocation) {
      console.warn("[LocationPermission] Geolocation is not supported by this browser.");
      return;
    }

    // Show the modal immediately when page loads (if not asked before)
    // Use requestAnimationFrame to ensure DOM is ready
    // Show the modal on every page load
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setShowModal(true);
      });
    });
  }, []);

  const handleAllow = () => {
    setIsRequesting(true);
    setShowModal(false);

    // Request location permission
    navigator.geolocation.getCurrentPosition(
      (_position) => {
        // Permission granted
        setIsRequesting(false);
      },
      (_error) => {
        // Permission denied or error
        setIsRequesting(false);
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 0,
      },
    );
  };

  const handleDeny = () => {
    setShowModal(false);
  };

  if (!showModal) return null;

  return (
    <CommonDialog
      open={showModal}
      onOpenChange={(open) => {
        if (!open) handleDeny();
      }}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      showCloseButton={false}
      size="md"
      title="Location Access Required"
      titleClassName="text-lg font-semibold text-black"
      description="We would like to access your location to provide you with a better experience. This helps us show you relevant content and services based on your location."
      descriptionClassName="text-base text-gray-700 py-4"
      primaryButton={
        <JetSmartButton
          className="bg-brand-blue h-auto p-3 px-6 text-white"
          onClick={handleAllow}
          disabled={isRequesting}
        >
          {isRequesting ? "Requesting..." : "Allow"}
        </JetSmartButton>
      }
      secondaryButton={
        <JetSmartButton
          className="bg-gray-200 h-auto p-3 px-6 text-gray-700 hover:bg-gray-300"
          onClick={handleDeny}
          disabled={isRequesting}
        >
          Deny
        </JetSmartButton>
      }
      footerClassName="flex flex-row gap-3 justify-end pt-4"
    />
  );
}
